import React, { useState } from 'react';
import {
  Box, Typography, TextField, IconButton, Button,
  Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, styled, Stack, InputAdornment, Divider,
  Container, Chip, Fade, Tooltip
} from '@mui/material';
import {
  Edit, Email, Phone, Lock,
  Visibility, VisibilityOff,
  AccountCircle as AccountCircleIcon,
  PhotoCamera as PhotoCameraIcon,
  DeleteOutline as DeleteOutlineIcon,
  FileUpload as FileUploadIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

/* ─── Design Tokens ─── */
const T = {
  amber:      '#C2823C',
  amberLight: '#E8A85A',
  amberPale:  'rgba(194,130,60,0.08)',
  sage:       '#6B8C6E',
  rose:       '#C4706A',
  cream:      '#FAF8F4',
  ivory:      '#FFFEFB',
  ink:        '#1a1510',
  inkMid:     '#4a3f30',
  inkLight:   '#8a7055',
  border:     'rgba(194,130,60,0.18)',
  shadow:     '0 8px 40px rgba(100,70,30,0.12)',
};

/* ─── Styled Components ─── */
const ProfileCard = styled(Box)(({ theme }) => ({
  background: T.ivory,
  border: `1px solid ${T.border}`,
  borderRadius: '28px',
  overflow: 'hidden',
  boxShadow: T.shadow,
  animation: 'fadeInUp 0.5s ease-out',
}));

/* The warm header band behind the avatar */
const HeroStrip = styled(Box)(() => ({
  background: `linear-gradient(135deg, ${T.amber}22 0%, ${T.amberLight}14 100%)`,
  borderBottom: `1px solid ${T.border}`,
  padding: '40px 32px 72px',
  textAlign: 'center',
  position: 'relative',
}));

const AvatarRing = styled(Box)(() => ({
  position: 'absolute',
  bottom: '-52px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 108,
  height: 108,
  borderRadius: '50%',
  padding: '4px',
  background: `linear-gradient(135deg, ${T.amber}, ${T.amberLight})`,
  boxShadow: `0 8px 28px ${T.amber}44`,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateX(-50%) scale(1.04)',
    boxShadow: `0 12px 36px ${T.amber}55`,
    '& .cam-overlay': { opacity: 1 },
  },
}));

const CamOverlay = styled(Box)(() => ({
  position: 'absolute',
  inset: 4,
  borderRadius: '50%',
  background: 'rgba(26,21,16,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.25s ease',
  backdropFilter: 'blur(2px)',
}));

const FieldRow = styled(Box)(() => ({
  padding: '14px 20px',
  borderRadius: '14px',
  background: T.cream,
  border: `1px solid ${T.border}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: `${T.amber}55`,
    background: T.ivory,
  },
}));

const SaveButton = styled(Button)(() => ({
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '50px',
  padding: '12px 40px',
  fontSize: '0.95rem',
  color: '#fff',
  background: `linear-gradient(135deg, ${T.amber} 0%, ${T.amberLight} 100%)`,
  boxShadow: `0 4px 20px ${T.amber}44`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 28px ${T.amber}55`,
  },
  '&.Mui-disabled': {
    background: `${T.amber}44`,
    color: '#fff',
  },
}));

/* ─── Photo Dialog ─── */
const PhotoDialog = ({ open, onClose, hasPhoto, onUpload, onRemove, loading }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden' } }}
  >
    <Box sx={{ background: `linear-gradient(135deg, ${T.amber}18, ${T.amberLight}0c)`, px: 3, pt: 3, pb: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.2rem', color: T.ink }}>
          Profile Photo
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: T.inkLight }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>

    <DialogContent sx={{ px: 3, py: 2.5 }}>
      <Stack spacing={1.5}>
        {/* Upload / Change option */}
        <Box
          onClick={onUpload}
          sx={{
            p: 2, borderRadius: '14px', border: `1.5px dashed ${T.amber}55`,
            display: 'flex', alignItems: 'center', gap: 2,
            cursor: 'pointer', transition: 'all 0.2s',
            '&:hover': { background: T.amberPale, borderColor: T.amber },
          }}
        >
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px',
            background: `${T.amber}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FileUploadIcon sx={{ color: T.amber, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, color: T.ink, fontSize: '0.9rem' }}>
              {hasPhoto ? 'Change photo' : 'Upload photo'}
            </Typography>
            <Typography sx={{ color: T.inkLight, fontSize: '0.75rem' }}>
              JPG, PNG or WebP · max 5 MB
            </Typography>
          </Box>
        </Box>

        {/* Remove option — only if photo exists */}
        {hasPhoto && (
          <Box
            onClick={onRemove}
            sx={{
              p: 2, borderRadius: '14px', border: `1.5px solid ${T.rose}30`,
              display: 'flex', alignItems: 'center', gap: 2,
              cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
              opacity: loading ? 0.6 : 1,
              '&:hover': { background: `${T.rose}08`, borderColor: `${T.rose}55` },
            }}
          >
            <Box sx={{
              width: 40, height: 40, borderRadius: '10px',
              background: `${T.rose}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {loading
                ? <CircularProgress size={18} sx={{ color: T.rose }} />
                : <DeleteOutlineIcon sx={{ color: T.rose, fontSize: 20 }} />
              }
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, color: T.rose, fontSize: '0.9rem' }}>
                Remove photo
              </Typography>
              <Typography sx={{ color: T.inkLight, fontSize: '0.75rem' }}>
                Revert to default avatar
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </DialogContent>
  </Dialog>
);

/* ─── Result Dialog ─── */
const ResultDialog = ({ open, onClose, title, message }) => {
  const isError = title?.toLowerCase() === 'error';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
          background: isError ? `${T.rose}15` : `${T.sage}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isError
            ? <ErrorOutlineIcon sx={{ color: T.rose, fontSize: 28 }} />
            : <CheckCircleOutlineIcon sx={{ color: T.sage, fontSize: 28 }} />
          }
        </Box>
        <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: '1.2rem', color: T.ink, mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ color: T.inkMid, fontSize: '0.9rem' }}>{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={onClose} sx={{
          borderRadius: '50px', px: 4, textTransform: 'none', fontWeight: 600,
          background: isError ? T.rose : T.sage, color: '#fff',
          '&:hover': { opacity: 0.88 },
        }}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─── Main Component ─── */
const ProfilePanel = ({ userId, profile, setProfile }) => {
  const [newName,            setNewName]            = useState(profile?.name || '');
  const [isEditingName,      setIsEditingName]      = useState(false);
  const [isEditingPassword,  setIsEditingPassword]  = useState(false);
  const [oldPassword,        setOldPassword]        = useState('');
  const [newPassword,        setNewPassword]        = useState('');
  const [confirmPassword,    setConfirmPassword]    = useState('');
  const [showOld,            setShowOld]            = useState(false);
  const [showNew,            setShowNew]            = useState(false);
  const [showConfirm,        setShowConfirm]        = useState(false);
  const [profilePhotoUrl,    setProfilePhotoUrl]    = useState('');
  const [saving,             setSaving]             = useState(false);
  const [removing,           setRemoving]           = useState(false);
  const [photoDialogOpen,    setPhotoDialogOpen]    = useState(false);
  const [resultDialog,       setResultDialog]       = useState({ open: false, title: '', message: '' });

  const showResult = (title, message) => setResultDialog({ open: true, title, message });

  /* Build the avatar src — prefer local override, then profile, then nothing */
  const avatarSrc = profilePhotoUrl
    || (profile?.profilePicture
        ? profile.profilePicture.startsWith('http')
          ? profile.profilePicture
          : `${process.env.REACT_APP_PROFILE_PHOTO}/${userId}/profile?ts=${Date.now()}`
        : null);

  const hasPhoto = !!(avatarSrc || profile?.profilePicture);

  /* ── File picker → upload ── */
  const handlePickAndUpload = () => {
    setPhotoDialogOpen(false);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setSaving(true);
      try {
        const renamedFile = new File([file], `${userId}.jpg`, { type: file.type });
        const formData = new FormData();
        formData.append('profilePicture', renamedFile);

        const res = await fetch(
          `${process.env.REACT_APP_UPLOAD_PROFILE_PICTURE_API_URL}/${userId}`,
          { method: 'POST', body: formData }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Upload failed');

        /* Cache-bust the new URL so it refreshes immediately */
        const freshUrl = `${process.env.REACT_APP_PROFILE_PHOTO}/${userId}/profile?ts=${Date.now()}`;
        setProfilePhotoUrl(freshUrl);
        setProfile(prev => ({ ...prev, profilePicture: data.filename }));
        showResult('Photo updated', data.message || 'Profile photo updated successfully!');
      } catch (err) {
        showResult('Error', err.message || 'Failed to upload photo');
      } finally {
        setSaving(false);
      }
    };
    input.click();
  };

  /* ── Remove photo ── */
  const handleRemovePhoto = async () => {
    setPhotoDialogOpen(false);
    setRemoving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_REMOVE_PROFILE_PICTURE_API_URL}/${userId}`,
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to remove');
      setProfilePhotoUrl('');
      setProfile(prev => ({ ...prev, profilePicture: '' }));
      showResult('Photo removed', 'Your profile photo has been removed.');
    } catch (err) {
      showResult('Error', err.message || 'Failed to remove photo');
    } finally {
      setRemoving(false);
    }
  };

  /* ── Save profile changes ── */
  const handleSave = async () => {
    if (newPassword && newPassword !== confirmPassword) {
      showResult('Password mismatch', 'New passwords do not match. Please try again.');
      return;
    }
    setSaving(true);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.put(`${process.env.REACT_APP_UPDATE_PROFILE_API}`, {
        userId,
        name:         newName     || profile?.name,
        email:        profile?.email,           // read-only — always send existing
        mobileNumber: profile?.mobileNumber,    // read-only — always send existing
        newPassword:  newPassword  || undefined,
        oldPassword:  oldPassword  || undefined,
      });
      const updated = res.data.user;
      setProfile(updated);
      setNewName(updated.name);
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
      setIsEditingName(false); setIsEditingPassword(false);
      showResult('Saved!', res.data.message || 'Your profile has been updated.');
    } catch (err) {
      showResult('Error', err.response?.data?.error || 'An unexpected error occurred.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <ProfileCard>

        {/* ── Hero strip ── */}
        <HeroStrip>
          <Typography sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 600, fontSize: '0.72rem',
            letterSpacing: '0.25em', textTransform: 'uppercase',
            color: T.amber, mb: 0.5,
          }}>
            Your Account
          </Typography>
          <Typography sx={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: '1.6rem', color: T.ink, lineHeight: 1.2,
          }}>
            {profile?.name || 'Welcome'}
          </Typography>
          <Typography sx={{ color: T.inkLight, fontSize: '0.82rem', mt: 0.5 }}>
            {profile?.email}
          </Typography>

          {/* Avatar with click-to-change */}
          <Tooltip title="Change profile photo" placement="bottom">
            <AvatarRing onClick={() => setPhotoDialogOpen(true)}>
              <Avatar
                src={avatarSrc || undefined}
                sx={{ width: '100%', height: '100%', background: T.cream }}
              >
                {!avatarSrc && <AccountCircleIcon sx={{ fontSize: 68, color: T.border }} />}
              </Avatar>
              <CamOverlay className="cam-overlay">
                <PhotoCameraIcon sx={{ color: '#fff', fontSize: 22 }} />
              </CamOverlay>
            </AvatarRing>
          </Tooltip>
        </HeroStrip>

        {/* ── Body ── */}
        <Box sx={{ pt: 8, px: { xs: 2.5, sm: 4 }, pb: 4 }}>

          {/* Name — editable */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: T.inkLight, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.8 }}>
              Full Name
            </Typography>
            <FieldRow sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonIcon sx={{ color: T.amber, fontSize: 18, flexShrink: 0 }} />
              {isEditingName ? (
                <TextField
                  variant="standard"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  autoFocus
                  fullWidth
                  InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', color: T.ink } }}
                />
              ) : (
                <Typography sx={{ flex: 1, fontSize: '0.9rem', color: T.ink }}>
                  {profile?.name || '—'}
                </Typography>
              )}
              <Tooltip title={isEditingName ? 'Cancel' : 'Edit name'}>
                <IconButton
                  size="small"
                  onClick={() => { setIsEditingName(!isEditingName); if (!isEditingName) setNewName(profile?.name || ''); }}
                  sx={{ color: isEditingName ? T.rose : T.inkLight, '&:hover': { color: isEditingName ? T.rose : T.amber } }}
                >
                  {isEditingName ? <CloseIcon fontSize="small" /> : <Edit fontSize="small" />}
                </IconButton>
              </Tooltip>
            </FieldRow>
          </Box>

          {/* Email — read only */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: T.inkLight, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Email Address
              </Typography>
              <Chip label="read only" size="small" sx={{ fontSize: '0.58rem', height: 16, bgcolor: `${T.amber}12`, color: T.amber, fontWeight: 700 }} />
            </Stack>
            <FieldRow sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: 0.75 }}>
              <Email sx={{ color: T.inkLight, fontSize: 18, flexShrink: 0 }} />
              <Typography sx={{ flex: 1, fontSize: '0.9rem', color: T.inkMid }}>{profile?.email || '—'}</Typography>
            </FieldRow>
          </Box>

          {/* Phone — read only */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.8 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: T.inkLight, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Phone Number
              </Typography>
              <Chip label="read only" size="small" sx={{ fontSize: '0.58rem', height: 16, bgcolor: `${T.amber}12`, color: T.amber, fontWeight: 700 }} />
            </Stack>
            <FieldRow sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: 0.75 }}>
              <Phone sx={{ color: T.inkLight, fontSize: 18, flexShrink: 0 }} />
              <Typography sx={{ flex: 1, fontSize: '0.9rem', color: T.inkMid }}>{profile?.mobileNumber || '—'}</Typography>
            </FieldRow>
          </Box>

          <Divider sx={{ borderColor: T.border, mb: 3 }} />

          {/* Password section */}
          <Button
            startIcon={<Lock sx={{ fontSize: '1rem !important' }} />}
            onClick={() => setIsEditingPassword(!isEditingPassword)}
            sx={{
              color: isEditingPassword ? T.rose : T.amber,
              fontWeight: 600, textTransform: 'none', fontSize: '0.85rem',
              borderRadius: '50px', px: 2, py: 0.8,
              background: isEditingPassword ? `${T.rose}10` : T.amberPale,
              mb: isEditingPassword ? 2 : 0,
              '&:hover': { background: isEditingPassword ? `${T.rose}18` : `${T.amber}14` },
            }}
          >
            {isEditingPassword ? 'Cancel password change' : 'Change password'}
          </Button>

          {isEditingPassword && (
            <Fade in>
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {[
                  { label: 'Current password',     value: oldPassword,     setter: setOldPassword,     show: showOld,     toggle: () => setShowOld(p => !p) },
                  { label: 'New password',          value: newPassword,     setter: setNewPassword,     show: showNew,     toggle: () => setShowNew(p => !p) },
                  { label: 'Confirm new password',  value: confirmPassword, setter: setConfirmPassword, show: showConfirm, toggle: () => setShowConfirm(p => !p) },
                ].map(f => (
                  <TextField
                    key={f.label}
                    fullWidth size="small"
                    type={f.show ? 'text' : 'password'}
                    label={f.label}
                    value={f.value}
                    onChange={e => f.setter(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={f.toggle} edge="end" size="small">
                            {f.show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ))}
              </Stack>
            </Fade>
          )}

          {/* Save button */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <SaveButton onClick={handleSave} disabled={saving} fullWidth>
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Changes'}
            </SaveButton>
          </Box>
        </Box>
      </ProfileCard>

      {/* ── Photo Dialog ── */}
      <PhotoDialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        hasPhoto={hasPhoto}
        onUpload={handlePickAndUpload}
        onRemove={handleRemovePhoto}
        loading={removing}
      />

      {/* ── Result Dialog ── */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog(r => ({ ...r, open: false }))}
        title={resultDialog.title}
        message={resultDialog.message}
      />
    </Container>
  );
};

export default ProfilePanel;